import { castArray, isEmpty, some } from 'lodash/fp';
import type { RouteProps } from 'react-router';
import type { Feature } from '../../../types/CompanyDtos';
import type { CurrentUserDto } from '../../../types/UserDtos';
import { useAppContext } from '../../react/hooks/useAppContext';
import { isFeatureEnabled } from '../../react/services/Features';
import type { CycleAdminFeature } from '../../react/services/User';
import { isAdmin, isAssistant, isCycleAdminFor, isHr } from '../../react/services/User';
import PageRoute from '../PageRoute';
import RouteError from '../RouteError';

type Role = 'HR Admin' | 'HR Assistant' | 'Tech Admin';

const ROLE_CHECKS: Record<Role, (user: CurrentUserDto) => boolean> = {
    'HR Admin': isHr,
    'HR Assistant': isAssistant,
    'Tech Admin': isAdmin,
};

/**
 * We don't support Route content via "children" or "component" because we only intercept "render".
 */
type Props = {
    /**
     * Current user must have this role (or at least one of these roles, if multiple).
     */
    requiredRole?: Role | Role[];
    /**
     * This feature flag must be enabled for the current company.
     */
    requiredFeature?: Feature;
    allowCycleAdminsFor?: CycleAdminFeature;
    errorRender?: RouteProps['render'];
} & Omit<RouteProps, 'children' | 'component'>;

export default function RestrictedRoute({
    render,
    requiredRole = [],
    requiredFeature,
    allowCycleAdminsFor,
    errorRender,
    ...otherProps
}: Props) {
    const { user } = useAppContext();
    let errorCode: number | null = null;
    const matchesRequiredFeature = !requiredFeature || isFeatureEnabled(requiredFeature);
    if (!matchesRequiredFeature) {
        errorCode = 404;
    }
    const matchesRequiredRole =
        isEmpty(requiredRole) ||
        some(role => ROLE_CHECKS[role](user), castArray(requiredRole)) ||
        (allowCycleAdminsFor && isCycleAdminFor(user, allowCycleAdminsFor));
    if (!matchesRequiredRole) {
        errorCode = 403;
    }
    let renderFn = render;
    if (errorCode) {
        renderFn = errorRender || (() => <RouteError errorCode={errorCode!} />);
    }
    return <PageRoute {...otherProps} render={renderFn} />;
}
